import React, { useState, useEffect, useCallback } from 'react';
import './TranslationTool.css';
import whole_genome from './whole_genome.png';
import transcriptome from './transcriptome.png';
import gut_health from './gut_health.png';
import oral_health from './oral_health.png';
import sports_performance from './sports_performance.png';
import oncology from './Oncorisk.png'


import ExtreGenomeModal from './ExtreGenomeModal';

const FLAGS = {
    'English': '🇬🇧',
    'Turkish': '🇹🇷',
    'Korean': '🇰🇷',
    'Japanese': '🇯🇵',
    'Chinese': '🇨🇳',
    'Russian': '🇷🇺',
    'Arabic': '🇸🇦',
    'Spanish': '🇪🇸'
};

// Function to determine which image to use based on report title/type
const getReportImage = (report) => {
    const title = report.title.toLowerCase();

    if (title.includes('transcriptome')) {
        return transcriptome;
    } else if (title.includes('gut') || title.includes('metagenome')) {
        return gut_health;
    } else if (title.includes('oral')) {
        return oral_health;
    } else if (title.includes('sports')) {
        return sports_performance;
    } else if (title.includes('oncoriskpro')) {
        return oncology;
    } else {
        // Default to whole genome
        return whole_genome;
    }
};

const groupTranslationsByParent = (translations, selectedLanguage) => {
    const groups = {};
    
    Object.entries(translations).forEach(([key, item]) => {
        const parts = key.split('.');
        const parentKey = parts.slice(0, -1).join('.');
        const lastPart = parts[parts.length - 1];
        
        if (!groups[parentKey]) {
            groups[parentKey] = {
                items: {},
                depth: parts.length - 1
            };
        }
        
        groups[parentKey].items[key] = {
            ...item,
            shortKey: lastPart,
            originalValue: item.translations[selectedLanguage] || ''
        };
    });
    
    return groups;
};

function TranslationTool({ userId }) {
    const [step, setStep] = useState(1);
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [translations, setTranslations] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [changes, setChanges] = useState(new Set());
    const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', ''
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState('');

    useEffect(() => {
        fetchReports();
    }, []);

    const handleLanguageChange = (e) => {
        const newLanguage = e.target.value;
        // Reset translations to original state when changing languages
        setTranslations(prev => {
            const reset = {};
            Object.entries(prev).forEach(([key, item]) => {
                reset[key] = {
                    ...item,
                    translations: { ...item.originalTranslations }
                };
            });
            return reset;
        });
        setSelectedLanguage(newLanguage);
        setChanges(new Set());
    };

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('https://service3.szapfs.org/translation/reports');
            if (!response.ok) throw new Error('Failed to fetch reports');
            const data = await response.json();
            setReports(data);
        } catch (error) {
            console.error('Error fetching reports:', error);
            setError('Failed to load reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchTranslations = async (reportId, report) => {
        const response = await fetch(`https://service3.szapfs.org/translation/report/${reportId}`);
        if (!response.ok) throw new Error('Failed to fetch report details');
        const data = await response.json();
        
        const groupedTranslations = {};
        data.forEach(item => {
            groupedTranslations[item.Key] = {
                id: item.id,
                key: item.Key,
                originalEnglish: item.English || '',
                translations: report.languages.reduce((acc, lang) => {
                    acc[lang] = item[lang] || '';
                    return acc;
                }, {}),
                originalTranslations: report.languages.reduce((acc, lang) => {
                    acc[lang] = item[lang] || '';
                    return acc;
                }, {})
            };
        });
        
        return groupedTranslations;
    };

    const handleReportSelect = async (report) => {
        try {
            setLoading(true);
            setError(null);
            setSelectedReport(report);
            setSelectedLanguage('');
            setChanges(new Set());
            setSaveStatus('');

            const groupedTranslations = await fetchTranslations(report.id, report);
            setTranslations(groupedTranslations);
            setStep(2);
        } catch (error) {
            console.error('Error fetching report details:', error);
            setError('Failed to load report details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleTranslationChange = (key, language, value) => {
        setTranslations(prev => {
            const original = prev[key].originalTranslations[language];
            const newChanges = new Set(changes);
            
            if (value !== original) {
                newChanges.add(key);
            } else {
                newChanges.delete(key);
            }
            
            setChanges(newChanges);
            setSaveStatus('');
            
            return {
                ...prev,
                [key]: {
                    ...prev[key],
                    translations: {
                        ...prev[key].translations,
                        [language]: value
                    }
                }
            };
        });
    };

    const handleSaveTranslations = async () => {
        if (!selectedLanguage || changes.size === 0) return;

        try {
            setSaveStatus('saving');
            const translationsArray = Array.from(changes).map(key => ({
                id: translations[key].id,
                Key: key,
                [selectedLanguage]: translations[key].translations[selectedLanguage]
            }));

            const response = await fetch(`https://service3.szapfs.org/translation/save/${selectedReport.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    translations: translationsArray,
                    targetLanguage: selectedLanguage
                })
            });

            if (!response.ok) throw new Error('Failed to save translations');
            
            // Refetch translations to ensure everything is in sync
            const updatedTranslations = await fetchTranslations(selectedReport.id, selectedReport);
            setTranslations(updatedTranslations);
            setChanges(new Set());
            setSaveStatus('saved');
            
            // Reset save status after 2 seconds
            setTimeout(() => {
                setSaveStatus('');
            }, 2000);
        } catch (error) {
            console.error('Error saving translations:', error);
            setError('Failed to save translations. Please try again.');
            setSaveStatus('');
        }
    };

    const handleOpenModal = (type) => {
        setModalType(type);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const groupedTranslations = groupTranslationsByParent(translations, selectedLanguage);

    const getSaveButtonClass = () => {
        if (saveStatus === 'saving') return 'save-button saving';
        if (saveStatus === 'saved') return 'save-button saved';
        return 'save-button';
    };

    const getSaveButtonText = () => {
        if (saveStatus === 'saving') return 'Saving...';
        if (saveStatus === 'saved') return 'Saved!';
        return `Save Changes (${changes.size})`;
    };

    const isGenomicsReport = () => {
        return selectedReport && selectedReport.id.toLowerCase().includes('genome');
    };

    return (
        <div className="translation-tool">
            {step === 1 ? (
                <div className="report-selection">
                    <div className="reports-grid">
                        {reports.map(report => (
                            <div key={report.id} className="report-card-wrapper">
                                <div
                                    className="report-card"
                                    onClick={() => handleReportSelect(report)}
                                >
                                    <img 
                                        src={getReportImage(report)}
                                        alt="Report illustration" 
                                        className="report-image"
                                    />
                                </div>
                                <h3>{report.title}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="translation-editor">
                    <div className="editor-header">
                        <div className="header-left">
                            <button onClick={() => setStep(1)} className="tran-back-button">
                                ← Reports
                            </button>
                            <select
                                value={selectedLanguage}
                                onChange={handleLanguageChange}
                                className="language-select"
                            >
                                <option value="">Select language</option>
                                {selectedReport.languages.map(lang => (
                                    <option key={lang} value={lang}>
                                       {lang}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="header-right">
                        {isGenomicsReport() && selectedLanguage && (
                            <div className="GenomicsButtons">
                                <button 
                                    className="traits-button"
                                    onClick={() => handleOpenModal('traits')}
                                >
                                    Traits
                                </button>
                                <button 
                                    className="omics-button"
                                    onClick={() => handleOpenModal('diseases')}
                                >
                                    Diseases
                                </button>
                            </div>
                        )}
                            <button 
                                onClick={handleSaveTranslations} 
                                className={getSaveButtonClass()}
                                disabled={!selectedLanguage || changes.size === 0 || saveStatus === 'saving'}
                            >
                            {getSaveButtonText()}
                        </button>
                        </div>
                    </div>

                    <div className="translation-sections">
                        {!selectedLanguage ? (
                            <div className="select-language-message">
                                Select a language to start translating
                            </div>
                        ) : (
                            Object.entries(groupedTranslations)
                                .sort((a, b) => a[1].depth - b[1].depth)
                                .map(([parentKey, group]) => (
                                    <div key={parentKey} className="translation-group">
                                        <div className="group-header">
                                            {parentKey || 'Root'}
                                        </div>
                                        <div className="translation-grid">
                                            {Object.entries(group.items).map(([key, item]) => (
                                                <div key={key} className="translation-section">
                                                    <div className="translation-content">
                                                        <div className="translation-editor-row">
                                                            <div className="source-text">
                                                                <div className="source-content">
                                                                    {item.originalEnglish}
                                                                </div>
                                                                <div className="translation-key">{item.shortKey}</div>
                                                            </div>
                                                            <div className="target-text">
                                                                <textarea
                                                                    value={item.translations[selectedLanguage]}
                                                                    onChange={(e) => handleTranslationChange(key, selectedLanguage, e.target.value)}
                                                                    placeholder={`${FLAGS[selectedLanguage]} Enter ${selectedLanguage} translation`}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                </div>
            )}
            
            <ExtreGenomeModal 
                isOpen={modalOpen} 
                onClose={handleCloseModal} 
                type={modalType} 
                selectedLanguage={selectedLanguage}
            />
        </div>
    );
}

export default TranslationTool;
